// src/pages/CicloDeVidaV2Page.jsx
// Ciclo de Vida V2 (solo admin o user Javier): TIMELINE / FLUJO.
// Arriba el flujo de estados desde 'Análisis preliminar' hasta 'Finalizado'
// (marcando el actual) y abajo una línea de tiempo con fechas REALES donde se
// ubican expedientes, tramitación/circuito, pagos (color por verificación),
// mediciones, hitos de obra y visitas — una suerte de timeline del proyecto.
// V1 (grafo radial) queda intacta en /ciclo-de-vida.
import React, { useContext, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { AuthContext } from '../context/authContext';
import { useSession } from '../hooks/useSession';

const COLOR_ESTADO = {
	'Finalizado': '#15803d',
	'En Ejecución': '#2563eb',
	'Seguimiento': '#d97706',
	'En circuito administrativo': '#9333ea',
	'Análisis preliminar': '#6b7280',
	'Adenda': '#ea580c',
	'En guarda': '#0d9488',
	'Desestimada': '#dc2626',
	'Rescisión': '#b45309',
};

const COLOR_FLUJO = {
	completado: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
	actual: { bg: '#dbeafe', text: '#1d4ed8', border: '#60a5fa' },
	pendiente: { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0' },
};

const VERIF_COLOR = {
	ok: '#16a34a',
	revisar: '#f59e0b',
	pendiente: '#94a3b8',
	sin: '#0ea5e9',
};

const VERIF_LABEL = { ok: 'OK', revisar: 'Revisar', pendiente: 'Pendiente', sin: 'Sin verificar' };

const toMs = (fecha) => Date.parse(`${fecha}T00:00:00Z`);

const fmtMonto = (val) => {
	const n = Number(val);
	if (!n || Number.isNaN(n)) return '-';
	return '$' + n.toLocaleString('es-AR');
};

export default function CicloDeVidaV2Page() {
	const { role, token } = useContext(AuthContext);
	const { authenticatedFetch } = useSession();

	const [q, setQ] = useState('');
	const [busqueda, setBusqueda] = useState('');
	const [cargando, setCargando] = useState(false);
	const [error, setError] = useState('');
	const [resultado, setResultado] = useState(null);

	const buscar = async (termino) => {
		const term = (termino || '').trim();
		if (!term) return;
		setBusqueda(term);
		setError('');
		setResultado(null);
		setCargando(true);
		try {
			const params = new URLSearchParams({ q: term });
			const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
			const res = await authenticatedFetch(`/api/obras/ciclo-vida-v2?${params.toString()}`, { headers });
			if (!res.ok) throw new Error(`Error ${res.status}`);
			setResultado(await res.json());
		} catch (err) {
			console.error('Error al buscar ciclo de vida V2:', err);
			setError(err.message);
		} finally {
			setCargando(false);
		}
	};

	const obra = resultado?.obra || null;
	const flujo = resultado?.flujo || null;
	const especial = resultado?.especial || null;
	const timeline = resultado?.timeline || null;
	const candidatos = resultado?.candidatos || null;
	const sinResultados = resultado?.sinResultados || false;

	const laneCount = timeline
		? new Set([
			...(timeline.estados || []).map(e => e.estado || 'Sin estado'),
			...timeline.eventos.map(e => e.estado || 'Sin estado'),
		]).size
		: 0;
	const chartHeight = Math.min(560, Math.max(320, 220 + laneCount * 54));

	// Opciones del timeline (solo cuando hay timeline).
	// Visualización: un CORREDOR de estados sobre el tiempo (bandas contiguas por
	// estado según el HISTORIAL real de obras_history) y, debajo, los eventos
	// (pagos, mediciones, etc.) colgando en la FILA de su estado: cada evento se
	// dibuja en el momento exacto dentro de la franja del estado en que ocurrió,
	// conectado al corredor por una línea fina.
	const option = useMemo(() => {
		if (!timeline) return null;

		const seriesDef = [
			{ tipo: 'inicio', color: '#6b7280', symbol: 'rect', size: 11, name: 'Inicio (carga)' },
			{ tipo: 'expediente', color: '#3b82f6', symbol: 'circle', size: 11, name: 'Expedientes' },
			{ tipo: 'tramitacion', color: '#8b5cf6', symbol: 'circle', size: 10, name: 'Tramitación' },
			{ tipo: 'circuito', color: '#a855f7', symbol: 'rect', size: 9, name: 'Circuito admin.' },
			{ tipo: 'obra', color: '#f59e0b', symbol: 'diamond', size: 13, name: 'Obra (inicio/fin)' },
			{ tipo: 'pago', color: '#0ea5e9', symbol: 'diamond', size: 12, name: 'Pagos' },
			{ tipo: 'medicion', color: '#14b8a6', symbol: 'circle', size: 11, name: 'Mediciones' },
			{ tipo: 'visita', color: '#ec4899', symbol: 'circle', size: 10, name: 'Visitas' },
		];

		// Filas = estados (orden del corredor primero, luego estados solo de eventos)
		const laneNames = [];
		const pushLane = (n) => { const nn = n || 'Sin estado'; if (!laneNames.includes(nn)) laneNames.push(nn); };
		(timeline.estados || []).forEach(se => pushLane(se.estado));
		timeline.eventos.forEach(e => pushLane(e.estado));
		const laneIdx = (n) => {
			const i = laneNames.indexOf(n || 'Sin estado');
			return i === -1 ? laneNames.length : i + 1; // +1 porque la fila 0 es el corredor
		};

		// Jitter horizontal para eventos que comparten fecha (evita superposiciones)
		const tsPorFecha = (() => {
			const byFecha = {};
			timeline.eventos.forEach(e => { (byFecha[e.fecha] = byFecha[e.fecha] || []).push(e); });
			const m = new Map();
			for (const [f, arr] of Object.entries(byFecha)) {
				arr.forEach((e, i) => {
					const off = (i - (arr.length - 1) / 2) * 1000 * 3600 * 9;
					m.set(e, toMs(f) + off);
				});
			}
			return m;
		})();
		const tsDe = (e) => tsPorFecha.get(e);

		// Corredor de estados (bandas contiguas).
		// Nota: metadatos van en bandasInfo (lookup por dataIndex) y en api.value —
		// en ECharts 5 "params.data" es undefined.
		const bandasInfo = timeline.estados.map((se, i) => ({
			estado: se.estado || 'Sin estado',
			inicio: se.inicio,
			fin: se.fin,
			color: COLOR_ESTADO[se.estado] || '#94a3b8',
			actual: i === timeline.estados.length - 1,
		}));
		const bandas = bandasInfo.map(b => ({
			value: [toMs(b.inicio), toMs(b.fin), b.color, b.estado, b.actual ? 1 : 0],
		}));
		const fondos = laneNames.map((nm, i) => ({
			value: [toMs(timeline.inicio), i + 1, toMs(timeline.fin), (COLOR_ESTADO[nm] || '#94a3b8') + '12'],
		}));
		const conectores = timeline.eventos.map(e => ({
			value: [tsDe(e), laneIdx(e.estado)],
		}));

		// Renderers custom
		const renderBanda = (params, api) => {
			const p0 = api.coord([api.value(0), 0]);
			const p1 = api.coord([api.value(1), 0]);
			const w = Math.max(0, p1[0] - p0[0]);
			const h = 44;
			const x = p0[0];
			const y = p0[1] - h / 2;
			const hex = api.value(2);
			const nombre = api.value(3);
			const actual = !!api.value(4);
			const children = [
				{
					type: 'rect',
					shape: { x, y, width: w, height: h, r: 5 },
					style: { fill: hex + '30', stroke: hex, lineWidth: actual ? 3 : 2 },
				},
			];
			if (w > 84) {
				children.push({
					type: 'text',
					style: { x: x + w / 2, y: p0[1], text: nombre, fill: hex, fontWeight: 'bold', fontSize: 12, textAlign: 'center', textVerticalAlign: 'middle' },
				});
			}
			return { type: 'group', children };
		};
		const renderFondo = (params, api) => {
			const x0 = api.coord([api.value(0), 0])[0];
			const x1 = api.coord([api.value(2), 0])[0];
			const y = api.coord([0, api.value(1)])[1];
			return { type: 'rect', shape: { x: x0, y: y - 21, width: Math.max(0, x1 - x0), height: 42, r: 6 }, style: { fill: api.value(3) } };
		};
		const renderConector = (params, api) => {
			const x = api.coord([api.value(0), 0])[0];
			const y0 = api.coord([0, 0])[1] + 22;
			const y1 = api.coord([0, api.value(1)])[1] - 21;
			return { type: 'line', shape: { x1: x, y1: y0, x2: x, y2: y1 }, style: { stroke: '#d3d8df', lineWidth: 1, lineDash: [4, 3] } };
		};

		const series = [
			{ name: 'bandas', type: 'custom', renderItem: renderBanda, data: bandas, z: 4, silent: false },
			{ name: 'fondos', type: 'custom', renderItem: renderFondo, data: fondos, z: 0, silent: true },
			{ name: 'conectores', type: 'custom', renderItem: renderConector, data: conectores, z: 1, silent: true },
			...seriesDef.map(def => {
				const pts = timeline.eventos.filter(e => e.tipo === def.tipo).map(e => {
					const itemStyle = { color: def.color, borderColor: COLOR_ESTADO[e.estado] || '#94a3b8', borderWidth: 2 };
					let symbolSize = def.size;
					if (e.tipo === 'pago') {
						itemStyle.color = VERIF_COLOR[e.verif] || VERIF_COLOR.sin;
						symbolSize = Math.min(26, 9 + Math.sqrt(e.montoNum || 0) / 150);
					}
					if (e.tipo === 'medicion') {
						symbolSize = Math.min(20, 8 + (e.pct || 0) / 5);
					}
					return {
						value: [tsDe(e), laneIdx(e.estado)],
						symbolSize: symbolSize + 2,
						itemStyle,
						extra: { fecha: e.fecha, etiqueta: e.etiqueta, detalle: e.detalle || [], tipo: e.tipo, verif: e.verif, estado: e.estado, desdeHistorial: e.desdeHistorial },
					};
				});
				return {
					name: def.name,
					type: 'scatter',
					symbol: def.symbol,
					data: pts,
					z: 3,
					...((def.tipo === 'inicio') ? {
						markLine: {
							symbol: 'none',
							label: { show: true, formatter: 'HOY', position: 'insideEndTop', color: '#ef4444', fontSize: 10 },
							lineStyle: { color: '#ef4444', type: 'dashed', width: 1 },
							data: [{ xAxis: toMs(timeline.hoy) }],
						},
					} : {}),
				};
			}),
		];

		return {
			backgroundColor: 'transparent',
			tooltip: {
				trigger: 'item',
				appendToBody: true,
				backgroundColor: 'rgba(255,255,255,0.97)',
				borderColor: '#e5e7eb',
				borderWidth: 1,
				textStyle: { color: '#1f2937', fontSize: 12 },
				formatter: (p) => {
					if (p.seriesIndex === 0 && bandasInfo[p.dataIndex]) {
						const b = bandasInfo[p.dataIndex];
						const hoy = timeline.hoy;
						const activa = b.actual ? ' · <b>ESTADO ACTUAL</b>' : '';
						return `<b style="color:${b.color}">▌ ${b.estado}</b>${activa}<br/>Desde: <b>${b.inicio}</b><br/>Hasta: <b>${b.fin}${b.actual && b.fin === hoy ? ' (hoy)' : ''}</b>`;
					}
					const d = p.data;
					if (!d) return p.seriesName;
					const x = d.extra || {};
					const lineas = [
						`<b>${x.etiqueta || p.seriesName}</b>`,
						`Fecha: <b>${x.fecha}</b>`,
						...(x.estado ? [`En estado: <b>${x.estado}</b>${x.desdeHistorial ? ' <span style="font-size:10px;color:#94a3b8">(según historial)</span>' : ''}`] : []),
						...(Array.isArray(x.detalle) ? x.detalle.map(([k, v]) => `${k}: <b>${v}</b>`) : []),
					];
					return lineas.join('<br/>');
				},
			},
			grid: { left: 150, right: 24, top: 14, bottom: 42 },
			xAxis: {
				type: 'time',
				min: toMs(timeline.inicio),
				max: toMs(timeline.fin),
				axisLabel: { formatter: (v) => {
					const d = new Date(v);
					return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
				}, fontSize: 10, color: '#6b7280' },
				axisLine: { lineStyle: { color: '#cbd5e1' } },
				splitLine: { show: true, lineStyle: { color: '#f1f5f9' } },
			},
			yAxis: {
				type: 'category',
				data: ['', ...laneNames],
				axisTick: { show: false },
				axisLine: { show: false },
				axisLabel: {
					show: true,
					fontWeight: 'bold',
					fontSize: 11,
					color: (v) => (v === '' ? 'rgba(0,0,0,0)' : (COLOR_ESTADO[v] || '#6b7280')),
					formatter: (v) => (v === '' ? '' : v),
					margin: 12,
				},
				splitLine: { show: false },
			},
			legend: {
				type: 'scroll',
				bottom: 0,
				icon: 'circle',
				itemWidth: 10,
				itemHeight: 10,
				textStyle: { fontSize: 10, color: '#374151' },
				data: seriesDef.map(s => s.name),
			},
			animationDuration: 500,
			series,
		};
	}, [timeline]);

	const estadoColor = obra ? (COLOR_ESTADO[obra.estado] || '#6b7280') : '#6b7280';

	// Resumen de eventos para los chips
	const resumen = useMemo(() => {
		if (!timeline) return null;
		const cont = {};
		const verif = { ok: 0, revisar: 0, pendiente: 0, sin: 0 };
		for (const e of timeline.eventos) {
			cont[e.tipo] = (cont[e.tipo] || 0) + 1;
			if (e.tipo === 'pago') verif[e.verif || 'sin'] = (verif[e.verif || 'sin'] || 0) + 1;
		}
		return { cont, verif };
	}, [timeline]);

	if (role !== 'admin') {
		return (
			<div className="p-6 text-center">
				<div className="p-6 max-w-md mx-auto mt-10 text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
					Acceso denegado. Solo administradores.
				</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="flex items-center gap-3 mb-2">
				<h1 className="text-2xl font-bold text-gray-800 dark:text-white">Ciclo de Vida V2 — Timeline</h1>
				<span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
					solo admin
				</span>
			</div>
			<p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
				Flujo de estados desde el análisis preliminar y línea de tiempo con pagos y mediciones en su fecha real.
			</p>

			{/* Buscador */}
			<form className="flex gap-2 mb-6" onSubmit={(e) => { e.preventDefault(); buscar(q); }}>
				<input
					type="text"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					placeholder="ID de obra o expediente..."
					className="flex-1 max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
				/>
				<button
					type="submit"
					disabled={cargando || !q.trim()}
					className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
				>
					{cargando ? 'Buscando...' : 'Ver timeline'}
				</button>
			</form>

			{error && (
				<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-md text-sm text-red-700 dark:text-red-300">
					Error: {error}
				</div>
			)}

			{!resultado && !cargando && !error && (
				<div className="p-10 text-center text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
					Ingresá un ID de obra o un número de expediente para visualizar el timeline.
				</div>
			)}

			{sinResultados && (
				<div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-md text-sm text-amber-700 dark:text-amber-300">
					No se encontró ninguna obra para “{busqueda}”.
				</div>
			)}

			{candidatos && (
				<div className="mb-6">
					<p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
						Se encontraron {candidatos.length} proyectos para “{busqueda}”. Elegí uno:
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
						{candidatos.map(c => (
							<button
								key={c.id}
								onClick={() => buscar(c.cg_01_id_partido_obra)}
								className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-left hover:border-blue-400 hover:shadow-sm transition-colors"
							>
								<div className="flex items-center justify-between mb-1">
									<span className="font-bold text-gray-800 dark:text-white">{c.cg_01_id_partido_obra}</span>
									<span
										className="px-2 py-0.5 text-[10px] font-semibold rounded-full text-white"
										style={{ backgroundColor: COLOR_ESTADO[c.cg_16_estado] || '#6b7280' }}
									>
										{c.cg_16_estado || 'Sin estado'}
									</span>
								</div>
								<div className="text-sm text-gray-600 dark:text-gray-300">{c.cg_02_nombre_obra}</div>
								<div className="text-xs text-gray-400 mt-1">{c.partido_nombre} · {c.area_responsable}</div>
							</button>
						))}
					</div>
				</div>
			)}

			{obra && flujo && timeline && (
				<>
					{/* Cabecera del proyecto */}
					<div className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
						<div className="flex flex-wrap items-start justify-between gap-4">
							<div>
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-2xl font-bold text-gray-800 dark:text-white">{obra.id_partido_obra}</span>
									<span
										className="px-2 py-0.5 text-xs font-semibold rounded-full text-white"
										style={{ backgroundColor: estadoColor }}
									>
										{obra.estado || 'Sin estado'}
									</span>
								</div>
								<div className="text-gray-600 dark:text-gray-300 mt-0.5">{obra.nombre}</div>
								<div className="text-xs text-gray-400 mt-1">
									{obra.partido} · {obra.area} · {obra.tipo_subsidio}
								</div>
							</div>
							<div className="flex gap-6 text-sm">
								<div>
									<div className="text-xs text-gray-400 uppercase">Monto</div>
									<div className="font-semibold text-gray-800 dark:text-white">{fmtMonto(obra.monto)}</div>
								</div>
								<div>
									<div className="text-xs text-gray-400 uppercase">Beneficiarios</div>
									<div className="font-semibold text-gray-800 dark:text-white">{Number(obra.beneficiarios || 0).toLocaleString('es-AR')}</div>
								</div>
								<div>
									<div className="text-xs text-gray-400 uppercase">Carga</div>
									<div className="font-semibold text-gray-800 dark:text-white">{obra.fecha_carga || '-'}</div>
								</div>
							</div>
						</div>

						{/* Flujo de estados */}
						<div className="mt-4 flex flex-wrap items-center gap-2">
							{flujo.map((f, i) => {
								const c = COLOR_FLUJO[f.estado];
								return (
									<React.Fragment key={f.nombre}>
										{i > 0 && <span className="text-gray-400">→</span>}
										<span
											className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
												f.estado === 'actual' ? 'ring-2 ring-blue-300' : ''
											}`}
											style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}
										>
											{f.nombre}
										</span>
									</React.Fragment>
								);
							})}
							{especial && (
								<>
									<span className="text-gray-400">→</span>
									<span
										className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ring-2 ring-orange-300"
										style={{ backgroundColor: '#ffedd5', color: '#ea580c', borderColor: '#fdba74' }}
									>
										{especial.nombre}
									</span>
								</>
							)}
						</div>

						{/* Resumen de eventos */}
						{resumen && (
							<div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
								<span className="text-gray-400">Eventos:</span>
								{Object.entries(resumen.cont).map(([k, v]) => (
									<span key={k} className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
										{k}: {v}
									</span>
								))}
								<span className="text-gray-400 ml-2">Pagos:</span>
								{Object.entries(resumen.verif).filter(([, v]) => v > 0).map(([k, v]) => (
									<span
										key={k}
										className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
										style={{ backgroundColor: VERIF_COLOR[k] + '1a', color: VERIF_COLOR[k] }}
									>
										<span className="h-2 w-2 rounded-full" style={{ backgroundColor: VERIF_COLOR[k] }} />
										{VERIF_LABEL[k] || k}: {v}
									</span>
								))}
								<button
									onClick={() => { setResultado(null); setQ(''); setBusqueda(''); }}
									className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline"
								>
									← Nueva búsqueda
								</button>
							</div>
						)}
					</div>

					{/* Timeline */}
					<div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
						{timeline.estados && (
							<div className="flex flex-wrap items-center gap-2 px-4 pt-3 text-xs">
								<span className="text-gray-400">Estados:</span>
								{timeline.estados.map((se, i) => {
									const hex = COLOR_ESTADO[se.estado] || '#94a3b8';
									const actual = i === timeline.estados.length - 1;
									return (
										<span
											key={se.estado + i}
											className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${actual ? 'ring-2' : ''}`}
											style={{
												backgroundColor: hex + '26',
												color: hex,
												borderColor: hex + '66',
												...(actual ? { boxShadow: `0 0 0 2px ${hex}55` } : {}),
											}}
										>
											<span className="h-2 w-2 rounded-full" style={{ backgroundColor: hex }} />
											{se.estado}
											{actual && <span className="font-bold">· hoy</span>}
										</span>
									);
								})}
							</div>
						)}
						{option && (
							<ReactECharts
								option={option}
								style={{ height: `${chartHeight}px`, width: '100%' }}
								opts={{ renderer: 'canvas' }}
							/>
						)}
						<p className="px-4 pb-3 text-[11px] text-gray-400">
							El corredor superior muestra los estados del proyecto a lo largo del tiempo según el historial de guardados. Los eventos cuelgan en la fila del estado en que ocurrieron (borde del color del estado) sobre su fecha real; diamante = pago (color por verificación), círculo = medición/expediente/visita. Línea roja punteada = hoy.
						</p>
					</div>
				</>
			)}
		</div>
	);
}